import { defineStore } from 'pinia';
import { AxiosError } from 'axios';
import { isPrimaryColor, safeLogoUrl, primaryTokens } from '../branding';
import {
  socialLinkList,
  supportContact,
  type TenantSocialLinks,
  type TenantSupport,
} from '@features/tenant/tenantSupport';
import { useAuthStore } from '@/stores/auth';
import {
  tenantService,
  type IdentityMode,
  type TenantBranding,
} from '@features/tenant/api/tenant.service';
import {
  DEFAULT_LAUNCHER_THEME,
  readDevThemeOverride,
  resolveLauncherTheme,
  type LauncherTheme,
} from '@features/tenant/launcherTheme';
import { themeTokens } from '@features/tenant/themeTokens';
import { useColorSchemeStore } from '@features/tenant/store/colorScheme';
import { normalizeCurrency } from '@shared/utils/money';

/**
 * `resolving` is the only honest initial state: until the hostname has been
 * resolved the app does not know whose colours, whose login or whose data it
 * is about to show, and rendering anything before that is how one tenant
 * briefly sees another's brand.
 *
 * `unknown-host` is terminal. No operation owns this hostname, and falling back
 * to a default would serve Legendaryum's experience under somebody else's
 * domain.
 */
export type TenantStatus =
  | 'resolving'
  | 'ready'
  | 'unknown-host'
  | 'unavailable';

interface TenantState {
  status: TenantStatus;
  resolveVersion: number;
  operationId: string;
  identityMode: IdentityMode | '';
  brandName: string;
  branding: TenantBranding | null;
  authProviders: string[];
  modules: string[] | null;
  theme: LauncherTheme;
  loginUrl: string;
  identityResolveUrl: string;
  support: TenantSupport | null;
  socialLinks: TenantSocialLinks | null;
  currency: string;
  currencyZeroDecimal: boolean;
}

export function safeExternalSsoLoginUrl(loginUrl: unknown): string {
  if (
    typeof loginUrl !== 'string' ||
    !loginUrl ||
    loginUrl.trim() !== loginUrl
  ) {
    return '';
  }
  if (
    Array.from(loginUrl).some((character) => {
      const codePoint = character.codePointAt(0);
      return (
        codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)
      );
    })
  ) {
    return '';
  }
  try {
    return new URL(loginUrl).protocol === 'https:' ? loginUrl : '';
  } catch {
    return '';
  }
}

const appliedThemeTokens: string[] = [];

const captureThemeMismatch = (operationId: string, value: string) => {
  console.warn('INVALID_LAUNCHER_THEME', { operationId, value });
};

export const useTenantStore = defineStore('tenant', {
  state: (): TenantState => ({
    status: 'resolving',
    resolveVersion: 0,
    operationId: '',
    identityMode: '',
    brandName: '',
    branding: null,
    authProviders: [],
    modules: null,
    theme: DEFAULT_LAUNCHER_THEME,
    loginUrl: '',
    identityResolveUrl: '',
    support: null,
    socialLinks: null,
    currency: 'USD',
    currencyZeroDecimal: false,
  }),

  getters: {
    isReady: (state) => state.status === 'ready',

    /** A delegated client owns its players; SIPLEI never holds their profile. */
    isDelegated: (state) => state.identityMode === 'delegated',

    sells: (state) => (module: string) => {
      if (state.status !== 'ready') return false;
      return state.modules === null || state.modules.includes(module);
    },

    launcherTheme: (state): LauncherTheme =>
      state.status === 'ready' ? state.theme : DEFAULT_LAUNCHER_THEME,

    supports: (state) => (provider: string) =>
      state.status === 'ready' && state.authProviders.includes(provider),

    externalSsoLoginUrl: (state) =>
      state.status === 'ready' && state.authProviders.includes('external_sso')
        ? safeExternalSsoLoginUrl(state.loginUrl)
        : '',

    primaryColor: (state) => {
      const color = state.branding?.primaryColor ?? '';
      return isPrimaryColor(color) ? color : '';
    },

    logoUrl: (state) => safeLogoUrl(state.branding?.logoUrl),

    showBrandName: (state) => state.branding?.showBrandName !== false,

    socialImageUrl: (state) => safeLogoUrl(state.branding?.socialImageUrl),

    supportContact: (state) =>
      supportContact(state.support ?? undefined, state.brandName),

    socialProfiles: (state) => socialLinkList(state.socialLinks ?? undefined),
  },

  actions: {
    async resolve() {
      const version = ++this.resolveVersion;
      const previousOperationId = this.operationId;
      this.status = 'resolving';
      this.operationId = '';
      this.identityMode = '';
      this.brandName = '';
      this.branding = null;
      this.authProviders = [];
      this.modules = null;
      this.theme = DEFAULT_LAUNCHER_THEME;
      this.loginUrl = '';
      this.identityResolveUrl = '';
      this.support = null;
      this.socialLinks = null;
      this.currency = 'USD';
      this.currencyZeroDecimal = false;
      this.applyBranding();
      try {
        const { data } = await tenantService.bootstrap();
        if (version !== this.resolveVersion) return;
        if (previousOperationId && previousOperationId !== data.operationId) {
          await useAuthStore().clearLocalSession();
          if (version !== this.resolveVersion) return;
        }
        this.operationId = data.operationId;
        this.identityMode = data.identityMode;
        this.brandName = data.brandName ?? '';
        this.branding = data.branding ?? null;
        this.authProviders = data.authProviders;
        this.modules = data.modules ?? null;
        this.theme = resolveLauncherTheme({
          configured: data.theme,
          override: readDevThemeOverride(
            window.location.search,
            import.meta.env.DEV,
          ),
          onUnknown: (value) => {
            captureThemeMismatch(data.operationId, value);
          },
        });
        this.loginUrl = data.loginUrl ?? '';
        this.identityResolveUrl = data.identityResolveUrl ?? '';
        this.support = data.support ?? null;
        this.socialLinks = data.socialLinks ?? null;
        this.currency = normalizeCurrency(data.currency);
        this.currencyZeroDecimal = data.currencyZeroDecimal ?? false;
        this.status = 'ready';
        this.applyBranding();
      } catch (error) {
        if (version !== this.resolveVersion) return;
        // A hostname nobody owns and a backend that is down are different
        // problems: the first is permanent and the second is worth retrying,
        // and telling the visitor "this address does not exist" when the API
        // is merely restarting would be a lie.
        this.status =
          error instanceof AxiosError && error.response?.status === 404
            ? 'unknown-host'
            : 'unavailable';
      }
    },

    applyBranding() {
      const root = document.documentElement;
      const style = root.style;
      style.removeProperty('--tenant-primary');
      const tokens = primaryTokens(this.primaryColor);
      if (tokens) {
        style.setProperty('--primary', tokens.primary);
        style.setProperty('--primary-foreground', tokens.foreground);
      } else {
        style.removeProperty('--primary');
        style.removeProperty('--primary-foreground');
      }

      for (const token of appliedThemeTokens) style.removeProperty(token);
      appliedThemeTokens.length = 0;

      const theme = this.launcherTheme;
      root.dataset.launcherTheme = theme;
      for (const [token, value] of Object.entries(
        themeTokens(theme, this.primaryColor, useColorSchemeStore().scheme),
      )) {
        style.setProperty(token, value);
        appliedThemeTokens.push(token);
      }
    },
  },
});
